import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categoryData, type CategoryData } from '@/mocks/categoryData';

const allCategorySlugs = Object.keys(categoryData);

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [category, setCategory] = useState<CategoryData | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (slug && categoryData[slug]) {
      setCategory(categoryData[slug]);
      setNotFound(false);
    } else if (slug) {
      setNotFound(true);
      setCategory(null);
    }
  }, [slug]);

  const handleCategorySwitch = (newSlug: string) => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    window.REACT_APP_NAVIGATE(`/category/${newSlug}`);
  };

  if (notFound) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center px-4">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-semibold text-foreground-950 mb-2">分类不存在</h1>
            <p className="text-sm text-foreground-600 mb-6">该分类可能已被移除或链接有误</p>
            <Link to="/" className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <div className="pt-20 md:pt-24 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 py-3" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <span className="text-foreground-950 font-medium">{category.name}</span>
            </nav>
          </div>
        </div>

        {/* Banner */}
        <section className="relative w-full h-48 md:h-64 overflow-hidden">
          <img src={category.image} alt={category.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 w-full max-w-7xl mx-auto px-4 md:px-6 pb-6 md:pb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-background-50/20 backdrop-blur-sm flex items-center justify-center">
                <i className={`${category.icon} text-xl text-background-50`}></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50">
                {category.name}
              </h1>
            </div>
            <p className="text-sm text-background-50/80">{category.description}</p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full bg-background-50 border-b border-background-200">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 md:gap-2 overflow-x-auto py-3 md:py-4 flex-wrap">
              {allCategorySlugs.map((catSlug) => (
                <button
                  key={catSlug}
                  onClick={() => handleCategorySwitch(catSlug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    slug === catSlug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  {categoryData[catSlug].name}
                  <span className={`ml-1.5 text-xs ${slug === catSlug ? 'text-background-50/70' : 'text-foreground-400'}`}>
                    {categoryData[catSlug].count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Article List */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {category.articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group flex flex-col bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 p-4 md:p-5 flex flex-col">
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 line-clamp-2 leading-snug mb-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 mb-4 flex-1">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>{article.expert}</span>
                      <span><i className="ri-eye-line mr-1"></i>{article.views} 观看</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {category.articles.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类暂无内容，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}